import { useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CarrierDispatchCenter,
  type DispatchRunSchedule,
  useUpdateCarrierDispatchCenter,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RunScheduleEditor } from "@/features/run-schedule-editor";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { hasHttpStatus } from "@/lib/api-error";

export function DeliveryScheduleSheet({
  center,
  onClose,
}: {
  center: CarrierDispatchCenter;
  onClose: () => void;
}) {
  const update = useUpdateCarrierDispatchCenter();
  const [processingMinutesText, setProcessingMinutesText] = useState(
    String(center.processingMinutes)
  );
  const [runSchedule, setRunSchedule] = useState<DispatchRunSchedule | null>(
    center.runSchedule
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [runDraftDirty, setRunDraftDirty] = useState(false);
  const submissionLock = useRef(false);
  const dirty =
    processingMinutesText !== String(center.processingMinutes) ||
    JSON.stringify(runSchedule) !== JSON.stringify(center.runSchedule) ||
    runDraftDirty;

  useUnsavedChangesGuard(
    dirty,
    "Leaving this page will discard your unsaved delivery-schedule changes. Continue?"
  );

  const close = () => {
    if (saving) return;
    if (
      dirty &&
      !window.confirm("Discard your unsaved delivery-schedule changes?")
    ) {
      return;
    }
    onClose();
  };

  const save = async () => {
    if (submissionLock.current) return;
    setError(null);
    const processingMinutes = Number(processingMinutesText);
    if (
      !Number.isInteger(processingMinutes) ||
      processingMinutes < 0 ||
      processingMinutes > 24 * 60
    ) {
      setError(
        "Processing time must be a whole number from 0 to 1,440 minutes."
      );
      return;
    }

    submissionLock.current = true;
    setSaving(true);
    try {
      await update.mutateAsync({
        dispatchCenterId: center.id,
        body: { processingMinutes, runSchedule },
      });
      toast.success("Delivery schedule saved.");
      onClose();
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to change delivery setup."
        : (response.response?.data?.error ??
          "Delivery schedule could not be saved.");
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setSaving(false);
    }
  };

  return (
    <DetailDrawer
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={`Trip schedule · ${center.name}`}
      description={`${center.code}${center.address?.city ? ` · ${center.address.city}` : ""} · ${center.timeZone}`}
      widthClassName="sm:max-w-3xl"
    >
      <div className="space-y-5" aria-busy={saving || undefined}>
        <label
          htmlFor={`processing-${center.id}`}
          className="block space-y-1 text-sm font-medium"
        >
          <span>Processing time (minutes)</span>
          <Input
            id={`processing-${center.id}`}
            aria-label="Processing time (minutes)"
            type="number"
            min={0}
            max={1440}
            step={1}
            inputMode="numeric"
            className="w-full sm:w-40"
            value={processingMinutesText}
            disabled={saving}
            onChange={(event) => {
              setProcessingMinutesText(event.target.value);
              setError(null);
            }}
          />
          <span className="block text-xs font-normal text-muted-foreground">
            Pickup slot end → packages sorted and ready for the next run.
          </span>
        </label>

        <fieldset disabled={saving} className="space-y-2">
          <h3 className="text-sm font-medium">Run schedule</h3>
          <RunScheduleEditor
            value={runSchedule}
            onDraftDirtyChange={setRunDraftDirty}
            onChange={(value) => {
              setRunSchedule(value);
              setError(null);
            }}
          />
        </fieldset>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        {saving && (
          <p role="status" className="text-sm text-muted-foreground">
            Saving delivery schedule…
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "No unsaved changes"}
          </span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="outline"
              size="touch"
              className="w-full sm:w-auto"
              disabled={saving}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              size="touch"
              className="w-full sm:w-auto"
              disabled={saving || !dirty}
              onClick={() => void save()}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </DetailDrawer>
  );
}
